import React, { useEffect, useState } from "react";
import {
  Create,
  TextInput,
  SimpleForm,
  required,
  SelectInput,
  ReferenceInput,
  RadioButtonGroupInput,
  Toolbar,
  FormDataConsumer,
  useRedirect,
  useRefresh,
  useDataProvider,
  CreateProps,
  ToolbarProps,
  BooleanInput,
} from "react-admin";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import SanitizedGrid from "components/SanitizedGrid";
import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import CustomImageInput from "components/CustomImageInput";
import LevelInput from "components/LevelInput";
import LevelGroupInput from "components/LevelGroupInput";
import DaysOfWeekInput from "components/DaysOfWeekInput";
import {
  AcademicYearResources,
  GetParam,
  sumCentsAmountToDolar,
} from "utils/tools";
import moment from "moment";
import AcademicYearInput from "components/AcademicYearInput";
import AddressAutoCompleteInput from "components/AddressAutoCompleteInput";
import SchoolInput from "components/SchoolInput";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import CustomStudentScheduleDateInput from "components/CustomStudentScheduleDateInput";
import UniqueCodeInput from "./components/UniqueCodeInput";
import CalculationButton from "./components/CalculationButton";
import SaveButton from "./components/SaveButton";
import { calculationCostCreateStudentValidation } from "utils/validation";
import NameInput from "../../components/NameInput";
import DropOffAuthInput from "./components/DropOffAuthInput";

const useStyles = makeStyles({
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
  },
});

const StudentCreate = (props: CreateProps) => {
  const param = GetParam();
  const [academicTerms, setAcademicTerms] = useState<IAcademicTerm[]>([]);
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [resultCalculations, setResultCalculations] = useState<
    IResultCalculation[]
  >([]);
  const [idAcademicYear, setIdAcademicYear] = useState<number>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCalculationButtonClicked, setIsCalculationButtonClicked] =
    useState(false);
  const [isNotTakeSchedule, setIsNotTakeSchedule] = useState(false);

  const classes = useStyles();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const provider = useDataProvider();

  const format = "DD/MM/YY";

  const breadcrumb = [
    {
      title: "Parent",
      link: `/parents/${param.get("parentId")}/show`,
    },
    {
      title: "Students",
      link: `/parents/${param.get("parentId")}/show/2`,
    },
    { title: "Create", link: "" },
  ];

  const CustomToolbar = (props: ToolbarProps) => {
    return (
      <Toolbar {...props}>
        <FormDataConsumer>
          {({ formData }) => (
            <div>
              <SaveButton
                invalid={props.invalid}
                isCalculationButtonClicked={isCalculationButtonClicked}
              />
            </div>
          )}
        </FormDataConsumer>
      </Toolbar>
    );
  };

  const CustomRedirect = () => {
    redirect(`/parents/${param.get("parentId")}/show/2`);
    refresh();
  };

  const callback = (value: number) => {
    setIdAcademicYear(value);
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);

  useEffect(() => {
    provider
      .getList("academicTerms", {
        filter: { AcademicYearId: Number(idAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setAcademicTerms(data as IAcademicTerm[]);
      });

    provider
      .getList("holidays", {
        filter: { AcademicYearId: Number(idAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setHolidays(data as IHoliday[]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  let includeDates: IIncludeDates[] = [];
  academicTerms.forEach((academicTerm: IAcademicTerm) => {
    let temp: IIncludeDates = {
      startDate: academicTerm.startDate,
      endDate: academicTerm.endDate,
    };

    includeDates.push(temp);
  });

  let excludeDates: IExcludeDates[] = [];
  holidays.forEach((holiday: IHoliday) => {
    let temp: IExcludeDates = {
      startDate: holiday.date,
    };

    excludeDates.push(temp);
  });

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <Typography variant="h4">New Student From Parent</Typography>
      <Create onSuccess={CustomRedirect} {...props}>
        <SimpleForm toolbar={<CustomToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Parent</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Name"
                source="parentId"
                reference="parents"
                disabled
              >
                <SelectInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Student Details</Typography>
            </Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Avatar"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={4}>
              <RadioButtonGroupInput
                source="gender"
                choices={[
                  { id: "Female", name: "Female" },
                  { id: "Male", name: "Male" },
                ]}
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <UniqueCodeInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <NameInput label="First Name" source="firstName" />
            </Grid>

            <Grid item xs={4}>
              <NameInput label="Last Name" source="lastName" />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <CustomDatePickerInput
                name="dateOfBirth"
                label="Date of Birth *"
                max={moment().format("Y-MM-DD")}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput label="Medical Condition" source="medicalCondition" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={3}>
              <TextInput label="MAC Address" source="macAddress" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <SchoolInput parentCallback={callback} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <LevelInput />
            </Grid>

            <Grid item xs={4}>
              <LevelGroupInput />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <DropOffAuthInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Schedule</Typography>
            </Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isNotTakeSchedule"
                label="Create Without Schedule"
                defaultValue={false}
                onChange={(e: boolean) => {
                  setIsNotTakeSchedule(e);
                  setIsLoaded(false);
                }}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            {!isNotTakeSchedule && (
              <>
                <Grid item xs={4}>
                  <AcademicYearInput parentCallback={callback} />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <CustomStudentScheduleDateInput
                    source="date"
                    label="Start Date *"
                    referenceSource={academicYearResources}
                    includeDates={includeDates}
                    excludeDates={excludeDates}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RadioButtonGroupInput
                    label="To and/or From"
                    source="wayType"
                    choices={[
                      { id: "OneWayMorning", name: "Morning (Bus to School)" },
                      {
                        id: "OneWayAfternoon",
                        name: "Afternoon (Bus from School)",
                      },
                      { id: "TwoWay", name: "Both" },
                    ]}
                    validate={required()}
                  />
                </Grid>

                <DaysOfWeekInput />

                <Grid item xs={12}>
                  <Typography variant="h6">
                    Pick up & Drop off Address
                  </Typography>
                </Grid>
            <Grid item xs={4}>
              <AddressAutoCompleteInput
                label="Postal Code *"
                source="postalCode"
                referenceSource="locationData"
              />
            </Grid>

            <Grid item xs={8}></Grid>

                <Grid item xs={2}>
                  <TextInput label="Unit No." source="unitNumber" />
                </Grid>
                <Grid item xs={2}>
                  <TextInput label="Block No." source="blockNumber" />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <TextInput label="Building Name" source="building" />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <TextInput
                    label="Street"
                    source="street"
                    validate={required()}
                  />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={12}>
                  {isLoaded && (
                    <SanitizedGrid container spacing={2}>
                      {resultCalculations.length > 0 ? (
                        <>
                          {resultCalculations.map(
                            (resulCalculation: IResultCalculation) => (
                              <Grid item xs={12}>
                                <Typography variant="h6">
                                  Cost Preview for Rate Chart #
                                  {resulCalculation.rateChart.id} -{" "}
                                  {resulCalculation.rateChart.name}
                                </Typography>
                                <TableContainer>
                                  <Table className={classes.table} size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Schedule Date</TableCell>
                                        <TableCell>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {resulCalculation.transactions.map(
                                        (item: IResultCalculationItem, i) => {
                                          return (
                                            <TableRow key={i}>
                                              <TableCell>
                                                {moment
                                                  .utc(item.fromDate)
                                                  .format(format)}{" "}
                                                -{" "}
                                                {moment
                                                  .utc(item.toDate)
                                                  .format(format)}
                                              </TableCell>
                                              <TableCell>
                                                $ {item.total.toFixed(2) / 100}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                      )}

                                      <TableRow>
                                        <TableCell>Total :</TableCell>
                                        <TableCell>
                                          ${" "}
                                          {sumCentsAmountToDolar(
                                            resulCalculation.transactions
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Grid>
                            )
                          )}
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            No Change in Cost.
                          </Typography>
                        </Grid>
                      )}
                    </SanitizedGrid>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormDataConsumer>
                    {({ formData }) => {
                      return (
                        <div>
                          <CalculationButton
                            invalid={calculationCostCreateStudentValidation(
                              formData
                            )}
                            setResultCalculations={setResultCalculations}
                            setIsLoaded={setIsLoaded}
                            setIsCalculationButtonClicked={
                              setIsCalculationButtonClicked
                            }
                            formData={formData}
                          />
                        </div>
                      );
                    }}
                  </FormDataConsumer>
                </Grid>
              </>
            )}
          </SanitizedGrid>
        </SimpleForm>
      </Create>
    </>
  );
};

export default StudentCreate;
